import { useOrganisations } from '@/hooks/useOrganisations';
import { useOrganisationsStore } from '@/store/organisations.store';
import { cn } from '@/utils/cn';
import { Building2, Check, ChevronDown, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface OrganisationSwitcherProps {
  onCreateClick: () => void;
}

export function OrganisationSwitcher({
  onCreateClick,
}: OrganisationSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: organisations } = useOrganisations();
  const { currentOrganisation, setCurrentOrganisation } =
    useOrganisationsStore();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative mb-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-x-3 rounded-lg bg-neutral-800 px-3 py-2.5 text-sm text-neutral-200 transition-colors hover:bg-neutral-700"
      >
        <Building2 className="h-4 w-4 shrink-0 text-neutral-400" />
        <span className="flex-1 truncate text-left">
          {currentOrganisation?.name || 'Sélectionner'}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-neutral-400 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-lg border border-neutral-700 bg-neutral-800 shadow-lg">
          <div className="max-h-48 overflow-y-auto py-1">
            {organisations?.map((org) => (
              <button
                key={org.uuid}
                type="button"
                onClick={() => {
                  setCurrentOrganisation(org);
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-x-2 px-3 py-2 text-sm transition-colors',
                  currentOrganisation?.uuid === org.uuid
                    ? 'bg-neutral-700 text-white'
                    : 'text-neutral-300 hover:bg-neutral-700/50'
                )}
              >
                <span className="flex-1 truncate text-left">{org.name}</span>
                {currentOrganisation?.uuid === org.uuid && (
                  <Check className="h-4 w-4 shrink-0 text-brand-400" />
                )}
              </button>
            ))}
          </div>
          <div className="border-t border-neutral-700">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onCreateClick();
              }}
              className="flex w-full items-center gap-x-2 px-3 py-2 text-sm text-neutral-400 transition-colors hover:bg-neutral-700/50 hover:text-white"
            >
              <Plus className="h-4 w-4" />
              <span>Créer une organisation</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
